import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { get } from 'lodash';

// Import notification type components
import PersonalMessageNotification from './types/PersonalMessageNotification';
import DayReminderNotification from './types/DayReminderNotification';
import AutoMailerUserNotification from './types/AutoMailerUserNotification';
import BcbAutoMailerGenericNotification from './types/BcbAutoMailerGenericNotification';
import EducationalContentNotification from './types/EducationalContentNotification';
import PartialSaleInterestNotification from './types/PartialSaleInterestNotification';
import RagProcessingNotification from './types/RagProcessingNotification';
import GenericNotification from './types/GenericNotification';

// GraphQL mutation to mark notification as read
const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($ids: [ID!]!) {
    readNotifications(ids: $ids)
  }
`;

const NotificationItem = ({ notification, updateCount, onNavigate }) => {
  const [isRead, setIsRead] = useState(!!notification.readAt);
  
  const [markAsRead] = useMutation(MARK_NOTIFICATION_AS_READ);

  const handleMarkAsRead = async () => {
    if (isRead) return;
    
    try {
      await markAsRead({
        variables: { ids: [notification.id] }
      });
      
      setIsRead(true);
      updateCount(1);
      
      console.log('✅ Notification marked as read:', notification.id);
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', error);
    }
  };

  const handleClick = () => {
    handleMarkAsRead();
    
    // Let the specific notification component handle navigation
    if (onNavigate) {
      onNavigate(notification);
    }
  };

  // Route to appropriate notification component based on action
  const renderNotification = () => {
    const action = get(notification, 'action', '');
    
    const commonProps = {
      notification,
      isRead,
      onClick: handleClick,
      onMarkAsRead: handleMarkAsRead,
    };

    // Use the correct action names from the original system
    switch (action) {
      case 'findbob.message_sent':
        return <PersonalMessageNotification {...commonProps} />;
      case 'findbob.bcp.send_day_reminder':
        return <DayReminderNotification {...commonProps} />;
      case 'findbob.bcb.auto_mailer_user':
        return <AutoMailerUserNotification {...commonProps} />;
      case 'findbob.bcb.auto_mailer_generic':
        return <BcbAutoMailerGenericNotification {...commonProps} />;
      case 'findbob.educational_content_created':
        return <EducationalContentNotification {...commonProps} />;
      case 'findbob.partial_sale.interest_expressed':
        return <PartialSaleInterestNotification {...commonProps} />;
      case 'rag_url_processing_complete':
      case 'rag_url_processing_failed':
        return <RagProcessingNotification {...commonProps} />;
      default:
        return <GenericNotification {...commonProps} />;
    }
  };

  return (
    <div
      className={`relative p-4 border-b border-succession-border cursor-pointer hover:bg-[var(--succession-hover-bg)] transition-colors ${
        !isRead ? 'bg-succession-primary-10' : ''
      }`}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!isRead && (
        <div className="absolute top-4 left-2 w-2 h-2 bg-succession-primary rounded-full"></div>
      )}

      {/* Notification content */}
      <div className={!isRead ? 'ml-4' : ''}>
        {renderNotification()}
      </div>
    </div>
  );
};

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    action: PropTypes.string.isRequired,
    readAt: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  updateCount: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
};

NotificationItem.defaultProps = {
  onNavigate: null,
};

export default NotificationItem; 